import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MdLock, MdVisibility, MdVisibilityOff, MdArrowBack } from 'react-icons/md';
import { verifyResetToken, resetPassword, clearForgotPasswordState } from '../redux/actions/authActions';

const ResetPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { resetPassword: resetPasswordState } = useSelector((state) => state.auth);
  
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationError, setValidationError] = useState('');

  const resetToken = searchParams.get('resetToken');
  const email = searchParams.get('email');

  useEffect(() => {
    if (!resetToken || !email) {
      navigate('/login');
      return;
    }

    // Verify the reset token when component mounts
    dispatch(verifyResetToken(resetToken, email));
  }, [dispatch, resetToken, email, navigate]);

  useEffect(() => {
    // If reset was successful, redirect to login after a delay
    if (resetPasswordState.success) {
      setTimeout(() => {
        dispatch(clearForgotPasswordState());
        navigate('/login');
      }, 3000);
    }
  }, [resetPasswordState.success, dispatch, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setValidationError('');
  };

  const validatePassword = () => {
    if (formData.newPassword.length < 8) {
      setValidationError('Password must be at least 8 characters long');
      return false;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setValidationError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validatePassword()) {
      return;
    }

    dispatch(resetPassword(resetToken, email, formData.newPassword));
  };

  const handleBackToLogin = () => {
    dispatch(clearForgotPasswordState());
    navigate('/login');
  };

  // Show loading while verifying token
  if (resetPasswordState.loading && !resetPasswordState.tokenValid) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img 
              src="https://washi.s3.amazonaws.com/RiskReport360.png"
              alt="Risk Report360 Logo" 
              className="login-logo"
            />
            <h1 className="login-title">Verifying Reset Link</h1>
            <p className="login-subtitle">Please wait...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error if token is invalid
  if (resetPasswordState.error && !resetPasswordState.tokenValid) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img 
              src="https://washi.s3.amazonaws.com/RiskReport360.png"
              alt="Risk Report360 Logo" 
              className="login-logo"
            />
            <h1 className="login-title">Invalid Reset Link</h1>
            <p className="login-subtitle">{resetPasswordState.error}</p>
          </div>
          
          <button 
            onClick={handleBackToLogin}
            className="back-to-login-btn"
          >
            <MdArrowBack />
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  // Show success message
  if (resetPasswordState.success) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img 
              src="https://washi.s3.amazonaws.com/RiskReport360.png"
              alt="Risk Report360 Logo" 
              className="login-logo"
            />
            <h1 className="login-title">Password Reset Successful</h1>
            <p className="login-subtitle">Your password has been reset successfully. Redirecting to login...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show reset password form
  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img 
            src="https://washi.s3.amazonaws.com/RiskReport360.png"
            alt="Risk Report360 Logo" 
            className="login-logo"
          />
          <h1 className="login-title">Reset Password</h1>
          <p className="login-subtitle">Enter your new password</p>
        </div>
        
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <div className="input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                placeholder="Enter new password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="input-wrapper">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm new password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <MdVisibilityOff /> : <MdVisibility />}
              </button>
            </div>
            {(validationError || resetPasswordState.error) && (
              <div className="error-message">
                {validationError || resetPasswordState.error}
              </div>
            )}
          </div>
          
          <button type="submit" className="login-btn" disabled={resetPasswordState.loading}>
            {resetPasswordState.loading ? 'Resetting...' : 'Reset Password'}
          </button>
          
          <button 
            type="button"
            onClick={handleBackToLogin}
            className="back-to-login-btn"
          >
            <MdArrowBack />
            Back to Login
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;